import { useState } from 'react';
import { format } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Building2, FileText, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { useCompany } from '@/hooks/useCompany';
import { useCompanyContractStatus } from '@/hooks/useCompanyContractStatus';
import { CompanyContractAcceptanceFlow } from '@/components/legal/CompanyContractAcceptanceFlow';
import { LegalDocPreviewDialog } from '@/components/legal/LegalDocPreviewDialog';

const REP_ROLE_LABELS: Record<string, string> = {
  owner: 'Sócio / Titular',
  admin: 'Administrador',
  procurator: 'Procurador',
};

export function CompanyContractSection() {
  const { company, isCompanyManager, isLoading: companyLoading } = useCompany();
  const { data: status, isLoading } = useCompanyContractStatus(company?.companyId);
  const [openModal, setOpenModal] = useState(false);
  const [previewSlug, setPreviewSlug] = useState<string | null>(null);

  // Only show for company managers
  if (companyLoading || !company || !isCompanyManager) {
    return null;
  }

  if (isLoading || !status) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Building2 className="w-5 h-5" />
            Contratos da empresa
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const { hasAcceptance, needsReacceptance, currentContract, currentDpa, latestAcceptance } = status;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Building2 className="w-5 h-5" />
          Contratos da empresa
        </CardTitle>
        <CardDescription>
          Contrato B2B e Acordo de Processamento de Dados (DPA) firmados por <strong>{company.companyName}</strong>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {needsReacceptance && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>
              {hasAcceptance ? 'Nova versão disponível' : 'Aceite pendente'}
            </AlertTitle>
            <AlertDescription>
              {hasAcceptance
                ? 'Há uma versão atualizada do Contrato B2B ou DPA. É necessário registrar um novo aceite.'
                : 'Esta empresa ainda não tem registro de aceite do Contrato B2B e DPA. Registre agora para manter conformidade.'}
            </AlertDescription>
          </Alert>
        )}

        {hasAcceptance && latestAcceptance ? (
          <ul className="divide-y">
            <li className="py-2 flex justify-between items-start gap-3 text-sm">
              <div className="min-w-0">
                <p className="font-medium">Contrato B2B</p>
                <p className="text-xs text-muted-foreground">
                  v{latestAcceptance.contract_version} • {format(new Date(latestAcceptance.accepted_at), 'dd/MM/yyyy HH:mm')}
                </p>
                <p className="text-xs text-muted-foreground">
                  Por {latestAcceptance.representative_name ?? '—'}
                  {' '}({REP_ROLE_LABELS[latestAcceptance.representative_role] ?? latestAcceptance.representative_role})
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPreviewSlug('b2b-contract')}
                className="text-xs text-primary underline whitespace-nowrap hover:text-primary/80"
              >
                <FileText className="inline w-3 h-3 mr-1" />
                Ver
              </button>
            </li>
            <li className="py-2 flex justify-between items-start gap-3 text-sm">
              <div className="min-w-0">
                <p className="font-medium">DPA — Tratamento de Dados</p>
                <p className="text-xs text-muted-foreground">
                  v{latestAcceptance.dpa_version} • {format(new Date(latestAcceptance.accepted_at), 'dd/MM/yyyy HH:mm')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPreviewSlug('dpa')}
                className="text-xs text-primary underline whitespace-nowrap hover:text-primary/80"
              >
                <FileText className="inline w-3 h-3 mr-1" />
                Ver
              </button>
            </li>
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">Nenhum aceite registrado para esta empresa.</p>
        )}

        {hasAcceptance && (currentContract || currentDpa) && (
          <div className="text-xs text-muted-foreground space-y-0.5">
            <p>
              Versão vigente do Contrato B2B:{' '}
              <Badge variant="outline" className="font-mono text-xs">v{currentContract?.version ?? '—'}</Badge>
            </p>
            <p>
              Versão vigente do DPA:{' '}
              <Badge variant="outline" className="font-mono text-xs">v{currentDpa?.version ?? '—'}</Badge>
            </p>
          </div>
        )}

        {hasAcceptance && latestAcceptance?.ip_address && (
          <details className="text-xs text-muted-foreground">
            <summary className="cursor-pointer">Detalhes técnicos do registro</summary>
            <div className="mt-2 space-y-1 pl-3 border-l">
              <p>IP: <span className="font-mono">{latestAcceptance.ip_address}</span></p>
              {latestAcceptance.user_agent && (
                <p className="break-all">User agent: <span className="font-mono text-[10px]">{latestAcceptance.user_agent}</span></p>
              )}
            </div>
          </details>
        )}

        {needsReacceptance && (
          <Button onClick={() => setOpenModal(true)} className="w-full">
            {hasAcceptance ? 'Aceitar nova versão' : 'Aceitar Contrato B2B + DPA'}
          </Button>
        )}
      </CardContent>

      <CompanyContractAcceptanceFlow
        open={openModal}
        onOpenChange={setOpenModal}
        companyId={company.companyId}
        companyName={company.companyName}
        onSuccess={() => setOpenModal(false)}
      />

      {previewSlug && (
        <LegalDocPreviewDialog
          slug={previewSlug}
          open={!!previewSlug}
          onOpenChange={(o) => !o && setPreviewSlug(null)}
        />
      )}
    </Card>
  );
}
